import { useEffect, useState } from "react";
import { ProjectWorkflowView } from "./views/ProjectWorkflowView";
import { AuditView } from "./views/AuditView";
import { CockpitView } from "./views/CockpitView";
import { SimoTWorkspaceView } from "./views/SimoTWorkspaceView";
import { SimoTAgentOsMissionConsoleView } from "./views/SimoTAgentOsMissionConsoleView";
import { SimoTAgentRoomView } from "./views/SimoTAgentRoomView";
import { useKeyboardSafeViewport } from "./useKeyboardSafeViewport";
import { surfaceFromHash, surfaceToHash, type SimoTSurface } from "./simotRouting";

const NAV: Array<{ id: SimoTSurface; label: string; description: string }> = [
  { id: "mission", label: "Mission Console", description: "Agent OS, Modelle, Regeln und Gates" },
  { id: "agent-room", label: "Agent Room", description: "Live-Orchestrierung: Lanes, Recovery, Delegation" },
  { id: "workspace", label: "Workspace", description: "Chat, Modelle und Voice" },
  { id: "projects", label: "Projects", description: "Planung und Ausführung" },
  { id: "cockpit", label: "Cockpit", description: "Systemlage" },
  { id: "audit", label: "Audit", description: "Nachvollziehbarkeit" },
];

export function SimoTDesktopApp(): JSX.Element {
  useKeyboardSafeViewport();
  // SIMO-OWNER-CONTROL-PLANE-ACCEPTANCE-RECOVERY-1 §4: aktive Surface kommt
  // aus der URL-Hash-Route — Reload, Deep-Link und Back/Forward bleiben in
  // Agent Room statt auf Mission Console zu springen.
  const [surface, setSurface] = useState<SimoTSurface>(() => surfaceFromHash(window.location.hash));

  useEffect(() => {
    const onHashChange = () => setSurface(surfaceFromHash(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const navigate = (next: SimoTSurface): void => {
    setSurface(next);
    const targetHash = surfaceToHash(next);
    if (window.location.hash !== targetHash) window.location.hash = targetHash;
  };

  useEffect(() => {
    document.title = "SimoT";
    document.querySelector('meta[name="application-name"]')?.setAttribute("content", "SimoT");
    document.querySelector('meta[name="description"]')?.setAttribute("content", "SimoT Agent OS Desktop");
    document.querySelector('link[rel="manifest"]')?.setAttribute("href", "/brand/simot-site.webmanifest");
    document.querySelector('link[rel="icon"][sizes="any"]')?.setAttribute("href", "/brand/simot-icon.ico");
    document.querySelector('link[rel="icon"][sizes="32x32"]')?.setAttribute("href", "/brand/simot-icon-32.png");
    document.querySelector('link[rel="icon"][sizes="192x192"]')?.setAttribute("href", "/brand/simot-icon-192.png");
    document.querySelector('link[rel="apple-touch-icon"]')?.setAttribute("href", "/brand/simot-icon-180.png");
  }, []);

  return (
    <div className="h-full min-h-0 w-full min-w-0 overflow-hidden flex flex-col bg-jarvis-bg text-jarvis-text">
      <header className="shrink-0 border-b border-jarvis-border bg-jarvis-surface/90 backdrop-blur-md">
        <div className="flex min-w-0 flex-wrap items-center gap-3 px-3 py-2.5 md:px-5">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="relative grid h-9 w-9 place-items-center rounded-lg border border-jarvis-accent/35 bg-jarvis-bg/80 shadow-[0_0_24px_rgba(212,164,73,0.16)]">
              <img src="/brand/simot-icon-64.png" alt="" className="h-7 w-7" />
              <span className="status-dot simo-accent-ok absolute -right-0.5 -top-0.5" style={{ width: 9, height: 9 }} />
            </div>
            <img src="/brand/simot-logo.svg" alt="SimoT" className="h-9 w-auto max-w-[180px]" />
          </div>

          <nav className="order-3 flex w-full min-w-0 items-center gap-1 overflow-x-auto simo-thin-scroll simo-scroll-fade-x md:order-none md:w-auto">
            {NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(item.id)}
                className={`shrink-0 rounded-md border px-3 min-h-[36px] text-xs transition-colors ${
                  surface === item.id
                    ? "border-jarvis-accent/50 bg-jarvis-accent/20 text-jarvis-text shadow-[0_0_18px_rgba(212,164,73,0.12)]"
                    : "border-transparent text-jarvis-muted hover:bg-jarvis-bg/60 hover:text-jarvis-text"
                }`}
                title={item.description}
                aria-current={surface === item.id ? "page" : undefined}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="order-2 ml-auto hidden min-w-0 md:order-none md:block" aria-hidden="true" />
        </div>
      </header>

      <main className="simo-dashboard-main flex-1 min-h-0 min-w-0 overflow-hidden">
        {surface === "mission" && <SimoTAgentOsMissionConsoleView />}
        {surface === "agent-room" && <SimoTAgentRoomView />}
        {surface === "workspace" && <SimoTWorkspaceView onOpenAgentOs={() => navigate("mission")} />}
        {surface === "projects" && <ProjectWorkflowView />}
        {surface === "cockpit" && <CockpitView />}
        {surface === "audit" && <AuditView />}
      </main>
    </div>
  );
}
